// customFieldsVmBuilder.js — a massa crua da API vira os VMs de customFields.vm.js. Puro: sem React e sem chamada.
import {
  CustomFieldDivergenceStatus,
  CustomFieldSystemKey,
  CustomFieldTarget,
  CustomFieldType,
  CustomFieldVisibility,
} from '../../catalogs/pageCatalog.generated';
import { presetsForTheme } from './customFieldPresets';

const T = CustomFieldType;

const TYPE_GROUPS = [
  { key: 'text', label: 'Texto', types: [T.TextShort, T.TextLong] },
  { key: 'numbers', label: 'Números e datas', types: [T.Number, T.Money, T.Date, T.Time] },
  { key: 'choices', label: 'Escolhas', types: [T.SingleChoice, T.MultiChoice, T.YesNo, T.Consent] },
  { key: 'contact', label: 'Documentos e contato', types: [T.Phone, T.Email, T.Cpf, T.Cnpj, T.Cep] },
];

const TYPE_LABELS = {
  [T.TextShort]: ['Texto curto', 'Uma linha. Bom para nomes e códigos.'],
  [T.TextLong]: ['Texto longo', 'Várias linhas. Bom para observações.'],
  [T.Number]: ['Número', 'Quantidade, idade, medida.'],
  [T.Money]: ['Valor em reais', 'Quanto a pessoa quer gastar, por exemplo.'],
  [T.Date]: ['Data', 'Dia, mês e ano.'],
  [T.Time]: ['Hora', 'Hora e minuto.'],
  [T.SingleChoice]: ['Escolha única', 'A pessoa marca uma opção.'],
  [T.MultiChoice]: ['Múltipla escolha', 'A pessoa marca várias opções.'],
  [T.YesNo]: ['Sim ou não', 'Duas respostas, com os textos que você quiser.'],
  [T.Consent]: ['Aceite', 'Uma caixa para marcar: termos, autorização.'],
  [T.Phone]: ['Telefone', 'Outro telefone além do principal.'],
  [T.Email]: ['E-mail', 'Outro e-mail além do principal.'],
  [T.Cpf]: ['CPF', 'Confere os dígitos verificadores.'],
  [T.Cnpj]: ['CNPJ', 'Confere os dígitos verificadores.'],
  [T.Cep]: ['CEP', '8 dígitos.'],
  [T.Address]: ['Endereço', 'CEP, rua, número, bairro, cidade e UF.'],
};

const INPUT_KINDS = {
  [T.TextShort]: 'text', [T.TextLong]: 'textarea', [T.Number]: 'number', [T.Money]: 'money', [T.Date]: 'date', [T.Time]: 'time',
  [T.MultiChoice]: 'multi', [T.YesNo]: 'yesNo', [T.Consent]: 'consent', [T.Phone]: 'phone', [T.Email]: 'email', [T.Cpf]: 'cpf',
  [T.Cnpj]: 'cnpj', [T.Cep]: 'cep', [T.Address]: 'address',
};

const ADDRESS_PARTS = [
  { key: 'zipCode', label: 'CEP' }, { key: 'street', label: 'Rua' }, { key: 'number', label: 'Número' },
  { key: 'complement', label: 'Complemento' }, { key: 'neighborhood', label: 'Bairro' }, { key: 'city', label: 'Cidade' }, { key: 'state', label: 'UF' },
];

const BASIC_DATA = [
  { systemKey: CustomFieldSystemKey.BirthDate, label: 'Data de nascimento', fieldType: T.Date, description: 'Vai para a ficha da pessoa.' },
  { systemKey: CustomFieldSystemKey.Cpf, label: 'CPF', fieldType: T.Cpf, description: 'Vai para a ficha da pessoa. Confere os dígitos.' },
  { systemKey: CustomFieldSystemKey.Gender, label: 'Gênero', fieldType: T.SingleChoice, description: 'Vai para a ficha da pessoa.' },
  { systemKey: CustomFieldSystemKey.Address, label: 'Endereço', fieldType: T.Address, description: 'Vai para a ficha da pessoa, com CEP, rua, número, bairro, cidade e UF.' },
];

const GENDER_OPTIONS = [
  { id: 'feminino', label: 'Feminino' }, { id: 'masculino', label: 'Masculino' }, { id: 'outro', label: 'Outro' }, { id: 'nao-informar', label: 'Prefiro não informar' },
];

const RULES = {
  [T.TextShort]: [
    { key: 'minLength', label: 'Tamanho mínimo', kind: 'number', hint: null },
    { key: 'maxLength', label: 'Tamanho máximo', kind: 'number', hint: 'Até 255 caracteres.' },
  ],
  [T.TextLong]: [
    { key: 'minLength', label: 'Tamanho mínimo', kind: 'number', hint: null },
    { key: 'maxLength', label: 'Tamanho máximo', kind: 'number', hint: 'Até 2.000 caracteres.' },
  ],
  [T.Number]: [
    { key: 'min', label: 'Mínimo', kind: 'number', hint: null },
    { key: 'max', label: 'Máximo', kind: 'number', hint: null },
    { key: 'decimals', label: 'Casas decimais', kind: 'number', hint: 'Sem preencher, só aceita número inteiro.' },
    { key: 'unit', label: 'Unidade', kind: 'text', hint: 'Por exemplo: anos, vezes.' },
  ],
  [T.Money]: [
    { key: 'min', label: 'Mínimo', kind: 'number', hint: null },
    { key: 'max', label: 'Máximo', kind: 'number', hint: null },
  ],
  [T.Date]: [
    { key: 'min', label: 'A partir de', kind: 'date', hint: null },
    { key: 'max', label: 'Até', kind: 'date', hint: null },
    { key: 'minDaysFromToday', label: 'Mínimo de dias a partir de hoje', kind: 'number', hint: 'Use número negativo para datas passadas.' },
    { key: 'maxDaysFromToday', label: 'Máximo de dias a partir de hoje', kind: 'number', hint: '0 impede data futura.' },
  ],
  [T.Time]: [
    { key: 'min', label: 'A partir de', kind: 'time', hint: null },
    { key: 'max', label: 'Até', kind: 'time', hint: null },
    { key: 'stepMinutes', label: 'De quantos em quantos minutos', kind: 'number', hint: null },
  ],
  [T.SingleChoice]: [
    { key: 'options', label: 'Opções', kind: 'options', hint: null },
    { key: 'display', label: 'Como mostrar', kind: 'select', hint: null, choices: [{ value: 0, label: 'Lista' }, { value: 1, label: 'Botões' }] },
  ],
  [T.MultiChoice]: [
    { key: 'options', label: 'Opções', kind: 'options', hint: null },
    { key: 'minSelected', label: 'Mínimo de opções', kind: 'number', hint: null },
    { key: 'maxSelected', label: 'Máximo de opções', kind: 'number', hint: null },
  ],
  [T.YesNo]: [
    { key: 'yesLabel', label: 'Texto do sim', kind: 'text', hint: 'Sem preencher, fica "Sim".' },
    { key: 'noLabel', label: 'Texto do não', kind: 'text', hint: 'Sem preencher, fica "Não".' },
  ],
  [T.Consent]: [{ key: 'text', label: 'Texto do aceite', kind: 'text', hint: 'O que a pessoa está aceitando.' }],
};

const NUMBER_KEYS = new Set(['minLength', 'maxLength', 'decimals', 'minDaysFromToday', 'maxDaysFromToday', 'stepMinutes', 'minSelected', 'maxSelected', 'display']);

// A tela é de todos os nichos: exemplo fixo serve a qualquer negócio. Pergunta de nicho vai em customFieldPresets.js.
const EXAMPLES = ['Como nos conheceu?', 'Alguma preferência?', 'Algum pedido especial?', 'Algo que devemos saber?'];

const LABELS = {
  title: 'Perguntas do agendamento',
  subtitle: 'Peça o que você precisa saber antes do atendimento',
  actionLabel: 'Nova pergunta',
  basicDataTitle: 'Dados básicos',
  basicDataHint: 'Ficam na ficha da pessoa e não contam no seu plano.',
  questionsTitle: 'Suas perguntas',
  reorderHint: 'Arraste para mudar a ordem no formulário.',
};

const FILTERS = [
  { key: 'services', label: 'Serviços', flag: 'filterServices', ids: 'serviceIds', wordAll: 'todos os serviços', noun: ['serviço', 'serviços'] },
  { key: 'professionals', label: 'Profissionais', flag: 'filterProfessionals', ids: 'professionalIds', wordAll: 'todos os profissionais', noun: ['profissional', 'profissionais'] },
  { key: 'places', label: 'Locais', flag: 'filterPlaces', ids: 'placeIds', wordAll: 'todos os locais', noun: ['local', 'locais'] },
];

const ANY_PROFESSIONAL_HINT = 'Em páginas com "qualquer profissional", só aparece se valer para todos.';
const REQUIRED_HINT = 'Vale só para quem agenda pela sua página.';
const REQUIRED_LABEL = 'Obrigatória na sua página';

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
const nameOf = (item) => item.displayName || item.name || item.title || '';

function liveNames(field, filter, lists) {
  const ids = field[filter.ids] || [];
  return (lists[filter.key] || []).filter((item) => ids.includes(item.id)).map(nameOf);
}

/** "Aparece em: Consulta · Dra. Ana · todos os locais". */
export function audienceLabel(field, lists) {
  const parts = FILTERS.map((filter) => {
    if (!field[filter.flag]) return filter.wordAll;
    const names = liveNames(field, filter, lists);
    if (names.length === 0) return `nenhum ${filter.noun[0]}`;
    return names.length <= 2 ? names.join(', ') : plural(names.length, filter.noun[0], filter.noun[1]);
  });
  return `Aparece em: ${parts.join(' · ')}`;
}

function fieldWarnings(field, lists) {
  const warnings = [];
  FILTERS.forEach((filter) => {
    if (field[filter.flag] && liveNames(field, filter, lists).length === 0) {
      warnings.push({
        key: 'emptyFilter',
        message: `Filtro de ${filter.label.toLowerCase()} sem nenhum item ativo: esta pergunta não aparece para ninguém.`,
        status: 'warning',
      });
    }
  });
  if (field.filterProfessionals) warnings.push({ key: 'anyProfessional', message: ANY_PROFESSIONAL_HINT, status: 'info' });
  return warnings;
}

function rulesLabel(field) {
  const config = field.config || {};
  if (config.options) return plural(config.options.length, 'opção', 'opções');
  if (config.min != null && config.max != null) return `De ${config.min} a ${config.max}`;
  if (config.min != null) return `A partir de ${config.min}`;
  if (config.max != null) return `Até ${config.max}`;
  if (config.maxLength != null) return `Até ${config.maxLength} caracteres`;
  return null;
}

function buildQuestionCard(field, lists, canCreate) {
  return {
    id: field.id,
    label: field.label,
    typeLabel: (TYPE_LABELS[field.fieldType] || [''])[0],
    rulesLabel: rulesLabel(field),
    targetLabel: field.target === CustomFieldTarget.Person ? 'Fica na ficha da pessoa' : 'Vale para este agendamento',
    visibilityLabel: field.visibility === CustomFieldVisibility.TeamOnly ? 'Só a equipe vê' : 'Quem agenda vê',
    requiredLabel: field.isRequired ? REQUIRED_LABEL : null,
    isActive: field.isActive,
    statusLabel: field.isActive ? 'Ativa' : 'Desativada',
    audienceLabel: audienceLabel(field, lists),
    warnings: field.isActive ? fieldWarnings(field, lists) : [],
    toggleLabel: field.isActive ? 'Desativar' : 'Ativar',
    canActivate: field.isActive || canCreate,
  };
}

function buildBasicRow(meta, field, lists) {
  const isActive = !!(field && field.id);
  return {
    systemKey: meta.systemKey,
    fieldId: isActive ? field.id : null,
    label: meta.label,
    description: meta.description,
    isActive,
    toggleLabel: isActive ? 'Não usar' : 'Usar',
    audienceLabel: isActive ? audienceLabel(field, lists) : null,
    requiredLabel: isActive && field.isRequired ? REQUIRED_LABEL : null,
    warnings: isActive ? fieldWarnings(field, lists) : [],
  };
}

/** Sem planos à venda não há limite para mostrar: nunca "5 de 3". */
function buildPlanUsage(limit, used, planName, plansSaleEnabled) {
  if (!plansSaleEnabled || limit == null) return null;
  const full = used >= limit;
  return {
    label: `${used} de ${limit} no plano ${planName}`,
    used,
    limit,
    status: full ? 'error' : used >= limit - 1 ? 'warning' : 'neutral',
    message: full
      ? `Seu plano comporta ${plural(limit, 'pergunta', 'perguntas')}. Para criar outra, mude de plano.`
      : `Você pode criar mais ${plural(limit - used, 'pergunta', 'perguntas')}.`,
    canCreate: !full,
  };
}

function buildSummary(questions) {
  const active = questions.filter((q) => q.isActive);
  const inactive = questions.length - active.length;
  const attention = active.filter((q) => q.warnings.some((w) => w.key === 'emptyFilter')).length;
  return {
    activeCount: active.length,
    activeLabel: plural(active.length, 'pergunta ativa', 'perguntas ativas'),
    inactiveLabel: inactive ? plural(inactive, 'desativada', 'desativadas') : null,
    attentionLabel: attention ? `${plural(attention, 'pergunta não aparece', 'perguntas não aparecem')} para ninguém` : null,
    status: attention ? 'warning' : 'neutral',
  };
}

/** Rascunho vazio da criação. `systemKey` = ligar dado básico. */
export function newDraft({ label = '', systemKey = null } = {}) {
  const meta = BASIC_DATA.find((b) => b.systemKey === systemKey);
  return {
    systemKey,
    label: meta ? meta.label : label,
    helpText: '',
    fieldType: meta ? meta.fieldType : T.TextShort,
    target: meta ? CustomFieldTarget.Person : CustomFieldTarget.Appointment,
    visibility: CustomFieldVisibility.ClientAndTeam,
    isRequired: false,
    rules: {},
    filters: { services: { mode: 'all', ids: [] }, professionals: { mode: 'all', ids: [] }, places: { mode: 'all', ids: [] } },
    labelError: null,
    ruleErrors: {},
    errorMessage: null,
    saving: false,
  };
}

/** Trocar de tipo zera as regras: as de um tipo não servem para o outro. */
export function changeFieldType(draft, fieldType) {
  return { ...draft, fieldType, rules: {}, ruleErrors: {} };
}

export function setFilterMode(draft, key, mode) {
  return { ...draft, filters: { ...draft.filters, [key]: { ...draft.filters[key], mode } } };
}

export function toggleFilterId(draft, key, id) {
  const { ids } = draft.filters[key];
  const next = ids.includes(id) ? ids.filter((item) => item !== id) : [...ids, id];
  return { ...draft, filters: { ...draft.filters, [key]: { ...draft.filters[key], ids: next } } };
}

/** Rascunho a partir de uma pergunta pronta: o dono ainda revê antes de criar. */
export function draftFromPreset(themeId, key) {
  const preset = presetsForTheme(themeId).find((p) => p.key === key);
  if (!preset) return newDraft();
  return {
    ...newDraft({ label: preset.label }),
    fieldType: preset.fieldType,
    target: preset.target ?? CustomFieldTarget.Appointment,
    rules: { ...preset.rules },
  };
}

function buildSuggestions(themeId, questions) {
  const used = new Set(questions.map((q) => q.label));
  return presetsForTheme(themeId)
    .filter((preset) => !used.has(preset.label))
    .map((preset) => ({ key: preset.key, label: preset.label, typeLabel: TYPE_LABELS[preset.fieldType][0] }));
}

function draftConfig(draft) {
  const config = {};
  (RULES[draft.fieldType] || []).forEach((rule) => {
    const value = draft.rules[rule.key];
    if (value === undefined || value === null || value === '') return;
    if (rule.kind === 'options') {
      config.options = value.map((option) => ({ id: option.id || '', label: option.label }));
    } else if (rule.kind === 'number' || NUMBER_KEYS.has(rule.key)) {
      config[rule.key] = Number(value);
    } else {
      config[rule.key] = value;
    }
  });
  return config;
}

/** Config da prévia e do envio: o dado básico leva a do catálogo, porque o servidor ignora a que vier. */
function previewConfig(draft) {
  if (draft.systemKey === CustomFieldSystemKey.Gender) return { options: GENDER_OPTIONS };
  return draftConfig(draft);
}

function draftAsField(draft) {
  const asField = {
    id: 'preview',
    label: draft.label.trim() || 'Sua pergunta',
    helpText: draft.helpText.trim() || null,
    fieldType: draft.fieldType,
    config: previewConfig(draft),
    isRequired: draft.isRequired,
    target: draft.target,
    visibility: draft.visibility,
  };
  FILTERS.forEach((filter) => {
    asField[filter.flag] = draft.filters[filter.key].mode === 'selected';
    asField[filter.ids] = draft.filters[filter.key].ids;
  });
  return asField;
}

function buildFilterField(filter, draft, lists) {
  const state = draft.filters[filter.key];
  const options = (lists[filter.key] || []).map((item) => ({ id: item.id, label: nameOf(item), isSelected: state.ids.includes(item.id) }));
  const count = options.filter((o) => o.isSelected).length;
  return {
    key: filter.key,
    label: filter.label,
    mode: state.mode,
    options,
    summaryLabel: state.mode === 'all' ? 'Todos' : plural(count, 'selecionado', 'selecionados'),
    warning: state.mode === 'selected' && count === 0 ? 'Selecione ao menos um, ou esta pergunta não aparece.' : null,
  };
}

function buildRuleField(rule, draft) {
  const fallback = rule.kind === 'options' ? [] : '';
  return {
    key: rule.key,
    label: rule.label,
    kind: rule.kind,
    value: draft.rules[rule.key] ?? fallback,
    hint: rule.hint,
    error: draft.ruleErrors[rule.key] || null,
    choices: rule.choices || [],
  };
}

function needsOptions(draft) {
  return draft.fieldType === T.SingleChoice || draft.fieldType === T.MultiChoice;
}

/** Conforto antes de enviar. Quem decide é o servidor. */
function canSaveDraft(draft) {
  if (draft.saving || !draft.label.trim()) return false;
  if (draft.systemKey) return true;
  if (needsOptions(draft)) {
    const options = draft.rules.options || [];
    if (options.length === 0 || options.some((o) => !o.label.trim())) return false;
  }
  if (draft.fieldType === T.Consent && !(draft.rules.text || '').trim()) return false;
  return true;
}

function buildCreator(draft, lists) {
  const isBasic = !!draft.systemKey;
  const isInternal = draft.visibility === CustomFieldVisibility.TeamOnly;
  const asField = draftAsField(draft);
  const filtersProfessional = draft.filters.professionals.mode === 'selected';
  const meta = BASIC_DATA.find((b) => b.systemKey === draft.systemKey);
  return {
    mode: isBasic ? 'basic' : 'question',
    title: isBasic ? `Usar ${meta.label.toLowerCase()}` : 'Nova pergunta',
    label: draft.label,
    labelError: draft.labelError,
    helpText: draft.helpText,
    typeGroups: isBasic ? [] : TYPE_GROUPS.map((group) => ({
      key: group.key,
      label: group.label,
      options: group.types.map((type) => ({
        value: type, label: TYPE_LABELS[type][0], description: TYPE_LABELS[type][1], isSelected: type === draft.fieldType,
      })),
    })),
    rules: isBasic ? [] : (RULES[draft.fieldType] || []).map((rule) => buildRuleField(rule, draft)),
    target: isBasic ? null : {
      label: 'Onde a resposta fica',
      value: draft.target,
      hint: 'Na ficha, a resposta volta preenchida no próximo agendamento da pessoa.',
      options: [
        { value: CustomFieldTarget.Appointment, label: 'Neste agendamento', description: 'Muda a cada atendimento. A pessoa responde toda vez.' },
        { value: CustomFieldTarget.Person, label: 'Na ficha da pessoa', description: 'Vale para todos os atendimentos. A pessoa responde uma vez.' },
      ],
    },
    visibility: {
      label: 'Quem vê',
      value: draft.visibility,
      hint: 'Pergunta só da equipe não aparece na sua página.',
      options: [
        { value: CustomFieldVisibility.ClientAndTeam, label: 'Quem agenda e a equipe', description: 'Aparece na sua página.' },
        { value: CustomFieldVisibility.TeamOnly, label: 'Só a equipe', description: 'Só nos agendamentos feitos por você e sua equipe.' },
      ],
    },
    isRequired: draft.isRequired && !isInternal,
    requiredLocked: isInternal,
    requiredHint: REQUIRED_HINT,
    filters: FILTERS.map((filter) => buildFilterField(filter, draft, lists)),
    audienceLabel: audienceLabel(asField, lists),
    anyProfessionalHint: filtersProfessional ? ANY_PROFESSIONAL_HINT : null,
    preview: buildBookingField(asField),
    immutableNotice: 'Depois de criada, só o rótulo muda. Para mudar o resto, apague e crie outra.',
    saveLabel: isBasic ? 'Usar' : 'Criar pergunta',
    canSave: canSaveDraft(draft),
    saving: draft.saving,
    errorMessage: draft.errorMessage,
  };
}

/** Corpo do POST: /customfields ou /customfields/system/{key}. */
export function buildCreatePayload(draft) {
  const isInternal = draft.visibility === CustomFieldVisibility.TeamOnly;
  const payload = {
    isRequired: draft.isRequired && !isInternal,
    filterServices: draft.filters.services.mode === 'selected',
    serviceIds: draft.filters.services.ids,
    filterProfessionals: draft.filters.professionals.mode === 'selected',
    professionalIds: draft.filters.professionals.ids,
    filterPlaces: draft.filters.places.mode === 'selected',
    placeIds: draft.filters.places.ids,
  };
  if (draft.systemKey) return { ...payload, label: draft.label.trim() };
  return {
    ...payload,
    label: draft.label.trim(),
    helpText: draft.helpText.trim() || null,
    fieldType: draft.fieldType,
    config: draftConfig(draft),
    target: draft.target,
    visibility: draft.visibility,
    isActive: true,
  };
}

/** Pergunta da API vira campo de formulário. */
export function buildBookingField(field, value = null, error = null) {
  const config = field.config || {};
  const isChoice = field.fieldType === T.SingleChoice;
  return {
    id: field.id,
    label: field.label,
    helpText: field.helpText || null,
    inputKind: isChoice ? (config.display === 1 ? 'radio' : 'select') : INPUT_KINDS[field.fieldType] || 'text',
    options: config.options || [],
    addressParts: field.fieldType === T.Address ? ADDRESS_PARTS : [],
    yesLabel: field.fieldType === T.YesNo ? config.yesLabel || 'Sim' : null,
    noLabel: field.fieldType === T.YesNo ? config.noLabel || 'Não' : null,
    consentText: field.fieldType === T.Consent ? config.text || '' : null,
    unit: config.unit || null,
    min: config.min ?? null,
    max: config.max ?? null,
    maxLength: config.maxLength ?? null,
    value,
    error,
    requiredMark: field.isRequired ? 'Obrigatória na página pública' : null,
  };
}

const isEmptyValue = (value) => {
  if (value === undefined || value === null || value === '') return true;
  if (Array.isArray(value)) return value.length === 0;
  // Endereço: a View manda o objeto mesmo depois de apagar todas as partes.
  return typeof value === 'object' && Object.values(value).every((part) => !String(part ?? '').trim());
};

/** O campo numérico da View devolve texto, e o servidor só aceita número. Texto inválido segue para o servidor recusar. */
function toApiValue(value, fieldId, fields) {
  if (fields.find((field) => field.id === fieldId)?.fieldType !== T.Number) return value;
  const number = Number(value);
  return Number.isFinite(number) ? number : value;
}

/** Só as preenchidas, no formato de `customFieldAnswers`. */
export function toAnswers(values, fields = []) {
  return Object.entries(values || {})
    .filter(([, value]) => !isEmptyValue(value))
    .map(([fieldId, value]) => ({ fieldId, value: toApiValue(value, fieldId, fields) }));
}

/** PUT de respostas: só o que mudou, e vazio vai null para a API apagar a resposta. */
export function toChangedAnswers(values, fields = []) {
  return Object.entries(values || {}).map(([fieldId, value]) => ({
    fieldId,
    value: isEmptyValue(value) ? null : toApiValue(value, fieldId, fields),
  }));
}

/** Respostas da ficha para as perguntas com destino pessoa. */
export function prefillFromPerson(fields, personValues) {
  const values = {};
  fields.filter((field) => field.target === CustomFieldTarget.Person).forEach((field) => {
    const saved = (personValues || []).find((item) => item.customFieldId === field.id)?.value;
    if (!isEmptyValue(saved)) values[field.id] = saved;
  });
  return values;
}

/** Erros por campo vindos de um 400 CUSTOM_FIELDS_INVALID. Null se o erro for outro. */
export function fieldErrorsFrom(error) {
  const body = error?.response?.data;
  if (body?.errorCode !== 'CUSTOM_FIELDS_INVALID') return null;
  return Object.fromEntries((body.fieldErrors || []).map((e) => [e.fieldId, e.message]));
}

export function buildCustomFieldsVm({
  loading = false, errorMessage = null, fields = [], services = [], professionals = [], places = [],
  limit = null, used = 0, planName = 'Grátis', plansSaleEnabled = false, draft = null, rename = null, confirmation = null,
  themeId = 'default',
} = {}) {
  const lists = { services, professionals, places };
  const planUsage = buildPlanUsage(limit, used, planName, plansSaleEnabled);
  const canCreate = planUsage ? planUsage.canCreate : true;
  const questions = fields.filter((f) => f.systemKey == null).map((f) => buildQuestionCard(f, lists, canCreate));
  const basicData = BASIC_DATA.map((meta) => buildBasicRow(meta, fields.find((f) => f.systemKey === meta.systemKey), lists));

  let viewState = 'ready';
  if (loading) viewState = 'loading';
  else if (errorMessage) viewState = 'error';
  else if (questions.length === 0) viewState = 'empty';

  return {
    surface: 'screen',
    block: null,
    viewState,
    errorMessage,
    labels: LABELS,
    summary: buildSummary(questions),
    planUsage,
    basicData,
    questions,
    suggestions: buildSuggestions(themeId, fields),
    emptyState: viewState === 'empty' ? {
      title: 'Nenhuma pergunta ainda',
      message: 'Pergunte o que você precisa saber antes do atendimento. As respostas chegam junto com o agendamento.',
      actionLabel: LABELS.actionLabel,
      examples: EXAMPLES,
    } : null,
    creator: draft ? buildCreator(draft, lists) : null,
    rename,
    confirmation,
  };
}

export function buildBookingQuestionsVm({ loading = false, errorMessage = null, fields = [], values = {}, errors = {} } = {}) {
  return {
    viewState: loading ? 'loading' : errorMessage ? 'error' : 'ready',
    errorMessage,
    title: 'Perguntas do agendamento',
    fields: fields.map((field) => buildBookingField(field, values[field.id] ?? null, errors[field.id] || null)),
  };
}

function buildDivergence({ valueId, title, valueText, personValueText, resolving, errorMessage }) {
  return {
    valueId,
    title,
    message: `Respondeu ${valueText}. Na ficha está ${personValueText || 'em branco'}.`,
    applyLabel: 'Usar esta',
    ignoreLabel: 'Ignorar',
    resolving: !!resolving && !errorMessage,
    errorMessage: errorMessage || null,
  };
}

export function buildAppointmentAnswersVm({
  loading = false, errorMessage = null, data = null, values = {}, errors = {}, saving = false, resolvingId = null, resolveError = null,
} = {}) {
  const answers = (data?.answers || []).map((answer) => {
    const pending = answer.divergenceStatus === CustomFieldDivergenceStatus.Pending;
    const edited = Object.prototype.hasOwnProperty.call(values, answer.customFieldId);
    return {
      id: answer.id,
      label: answer.label,
      valueText: answer.valueText,
      field: buildBookingField(
        { ...answer, id: answer.customFieldId, config: answer.config, isRequired: false },
        edited ? values[answer.customFieldId] : answer.value,
        errors[answer.customFieldId] || null,
      ),
      divergence: pending ? buildDivergence({
        valueId: answer.id,
        title: answer.label,
        valueText: answer.valueText,
        personValueText: answer.personValueText,
        resolving: resolvingId === answer.id,
        errorMessage: resolvingId === answer.id ? resolveError : null,
      }) : null,
    };
  });
  const unanswered = (data?.unanswered || []).map((field) => buildBookingField(field, values[field.id] ?? null, errors[field.id] || null));

  return {
    viewState: loading ? 'loading' : errorMessage ? 'error' : 'ready',
    errorMessage,
    title: 'Respostas',
    answers,
    unanswered,
    unansweredLabel: unanswered.length ? 'Ainda sem resposta' : null,
    isDirty: Object.keys(values).length > 0,
    saving,
  };
}

export function buildPersonFieldsVm({ loading = false, errorMessage = null, data = null, resolvingId = null, resolveError = null } = {}) {
  return {
    viewState: loading ? 'loading' : errorMessage ? 'error' : 'ready',
    errorMessage,
    title: 'Dados pedidos no agendamento',
    values: (data?.values || []).map((value) => ({
      fieldId: value.customFieldId,
      label: value.label,
      valueText: value.valueText || 'Não informado',
      isEmpty: !value.valueText,
    })),
    divergences: (data?.divergences || []).map((item) => buildDivergence({
      valueId: item.valueId,
      title: item.label,
      valueText: item.valueText,
      personValueText: item.personValueText,
      resolving: resolvingId === item.valueId,
      errorMessage: resolvingId === item.valueId ? resolveError : null,
    })),
  };
}
